import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from './store-hook';
import useGoogleValidation from './googleValidation-hook';
import useGoogleGeocode from './googleGeocode-hook';
import { MysteryShopperAddressDto, MysteryShopperAddressType, MysteryShopperClient, PostalAddressDto } from '../api/api.g';
import { Pressable } from 'react-native';
import { useDispatch } from 'react-redux';
import { lightBoxActions } from '../store/lightBoxSlice';
import { useMutation } from '@tanstack/react-query';
import AddressRequestPopup from '../components/onboarding/AddressRequestPopup';
import useApi from './api-hook';
import AddressForm from '../components/address/AddressForm';
import Label from '../components/common/Label';
import ProfileModal from '../components/myaccount/myprofile/ProfileModal';
import Popup from '../components/common/Popup';
import useTranslation from './translation-hook';
import useCustomTheme from './theme-hook';
import useUserPrefsService from './userPrefsService-hook';

const useAddressMangager = () => {
  const userPrefs = useAppSelector((selector) => selector.userPrefs);
  const t = useTranslation();
  const apiMysteryShopper = useApi(MysteryShopperClient);
  const googleValidator = useGoogleValidation();
  const userPrefService = useUserPrefsService();
  const dispatch = useDispatch();
  const googleGeocode = useGoogleGeocode();
  const theme = useCustomTheme();
  const [loading, setLoading] = useState(false);
  const [showAddressPopup, setShowAddressPopup] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [suspiciousAddress, setSuspiciousAddress] = useState(false);
  const [confirmedAddress, setConfirmedAddress] = useState(false);
  const [dto, setDto] = useState<MysteryShopperAddressDto>(undefined);
  const [isDefaultLocation, setIsDefaultLocation] = useState<boolean>(undefined);
  const [newDto, setNewDto] = useState<MysteryShopperAddressDto>(undefined);
  const [callback, setCallback] = useState<() => void>(undefined);
  const [awaitingAddressRefresh, setAwaitingAddressRefresh] = useState(false);
  const lastResolvedLocationKey = useRef<string>(undefined);

  const getLocationKey = (lat?: number, lng?: number, isDefault?: boolean) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return undefined;
    }

    return `${isDefault ? 1 : 0}:${Number(lat).toFixed(4)}:${Number(lng).toFixed(4)}`;
  };

  useEffect(() => {
    if (!showAddressPopup) {
      return;
    }

    const currentLocation = userPrefs.currentLocation;
    if (!currentLocation) {
      return;
    }

    const usingDefaultLocation = !!currentLocation.isDefault;
    setIsDefaultLocation(usingDefaultLocation);

    const locationKey = getLocationKey(currentLocation.lat, currentLocation.lng, usingDefaultLocation);
    if (locationKey && lastResolvedLocationKey.current === locationKey) {
      return;
    }

    if (usingDefaultLocation) {
      lastResolvedLocationKey.current = locationKey;
      setDto({
        address: '',
        city: '',
        postalCode: '',
        department: '',
        country: '',
        longitude: currentLocation.lng,
        latitude: currentLocation.lat,
        type: MysteryShopperAddressType.Home,
      } as MysteryShopperAddressDto);
      return;
    }

    setDto(undefined);

    let isCancelled = false;
    googleGeocode(currentLocation, (results: any) => {
      if (isCancelled) {
        return;
      }

      lastResolvedLocationKey.current = locationKey;
      setDto({
        address: results.address,
        city: results.location,
        postalCode: results.postalCode,
        department: results.province,
        country: results.country,
        longitude: currentLocation.lng,
        latitude: currentLocation.lat,
        type: MysteryShopperAddressType.Home,
      } as MysteryShopperAddressDto);
    });

    return () => {
      isCancelled = true;
    };
  }, [showAddressPopup, userPrefs.currentLocation]);

  useEffect(() => {
    if (!awaitingAddressRefresh || userPrefs?.addresses === undefined) {
      return;
    }

    const hasServiceAddress = userPrefs.addresses.some((x: any) => x.type != MysteryShopperAddressType.Current);
    if (hasServiceAddress) {
      setAwaitingAddressRefresh(false);
    }
  }, [awaitingAddressRefresh, userPrefs?.addresses]);

  const addAddressMutation = useMutation({
    mutationFn: (dto: MysteryShopperAddressDto) => {
      return apiMysteryShopper.addAddress(dto);
    },
  });

  const consumeCallback = () => {
    const currentCallback = callback;
    setCallback(undefined);
    return currentCallback;
  };

  const addAddressAndRefreshProfile = (addressDto: MysteryShopperAddressDto, onDone?: () => void) => {
    setAwaitingAddressRefresh(true);
    addAddressMutation.mutate(addressDto, {
      onSuccess: () => {
        userPrefService.updateUserProfileInfo(() => {
          setAwaitingAddressRefresh(false);
          onDone && onDone();
        });
      },
      onError: () => {
        setAwaitingAddressRefresh(false);
      },
    });
  };

  const handleOnConfirmAddressRequest = () => {
    const onDone = consumeCallback();
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setNoDismiss(false));
    dispatch(lightBoxActions.setContent(undefined));
    setShowAddressPopup(false);
    addAddressAndRefreshProfile(dto, onDone);
  };

  const handleOnEditAddressRequest = () => {
    dispatch(lightBoxActions.setNoDismiss(true));
    setConfirmedAddress(false);
    setShowAddressPopup(false);
    setShowAddressForm(true);
    setNewDto(dto);
    dispatch(lightBoxActions.setVisibility(true));
    dispatch(
      lightBoxActions.setOnClose(() => {
        handleOnCloseAddressForm();
      }),
    );
  };

  const handleConfirmAddressForm = (newDto, bypassValidation?: boolean) => {
    const addressDtoForSave = new MysteryShopperAddressDto();
    addressDtoForSave.init(newDto);

    if (!Number.isFinite(addressDtoForSave.latitude) || !Number.isFinite(addressDtoForSave.longitude)) {
      addressDtoForSave.latitude = dto?.latitude;
      addressDtoForSave.longitude = dto?.longitude;
    }
    if (bypassValidation) {
      const onDone = consumeCallback();
      setSuspiciousAddress(false);
      setConfirmedAddress(true);
      setShowAddressForm(false);
      dispatch(lightBoxActions.setVisibility(false));
      dispatch(lightBoxActions.setOnClose(undefined));
      addAddressAndRefreshProfile(addressDtoForSave, onDone);
    } else {
      if (newDto && newDto.address && newDto.city && newDto.postalCode && newDto.department && newDto.country) {
        setLoading(true);

        var data = new PostalAddressDto();

        data.civicAddress = newDto.address;
        data.city = newDto.city;
        data.postalCode = newDto.postalCode;
        data.department = newDto.department;
        data.country = newDto.country;

        googleValidator(data, (valid) => {
          setLoading(false);
          if (valid) {
            const onDone = consumeCallback();
            setSuspiciousAddress(false);
            setConfirmedAddress(true);
            setShowAddressForm(false);
            dispatch(lightBoxActions.setVisibility(false));
            dispatch(lightBoxActions.setOnClose(undefined));
            addAddressAndRefreshProfile(addressDtoForSave, onDone);
          } else {
            setSuspiciousAddress(true);
          }
        });
      } else {
        setSuspiciousAddress(true);
      }
    }
  };

  const handleOnCloseAddressForm = () => {
    const onDone = consumeCallback();
    setShowAddressForm(false);
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setContent(undefined));

    if (!confirmedAddress) {
      addAddressAndRefreshProfile(dto, onDone);
    } else {
      onDone && onDone();
    }

    dispatch(lightBoxActions.setOnClose(undefined));
  };

  useEffect(() => {
    if (showAddressPopup && dto) {
      dispatch(
        lightBoxActions.setContent(
          <AddressRequestPopup isDefaultLocation={isDefaultLocation} dto={dto} onConfirm={handleOnConfirmAddressRequest} onEdit={handleOnEditAddressRequest} />,
        ),
      );
    }
  }, [showAddressPopup, dto, isDefaultLocation]);

  useEffect(() => {
    if (showAddressForm) {
      dispatch(
        lightBoxActions.setContent(
          <Popup scrollable={false} screenMargins={false}>
            <ProfileModal
              hideCloseButton
              onClose={handleOnCloseAddressForm}
              title={t('address')}
              onConfirm={() => handleConfirmAddressForm(newDto)}
              loading={loading}
            >
              {
                <>
                  {suspiciousAddress && (
                    <Label style={{ marginBottom: 24, color: theme.text.error, fontSize: 16, fontWeight: '600', flexShrink: 1, maxWidth: 260 }} numberOfLines={5}>
                      {t('address-incorrect-check-again')}
                    </Label>
                  )}
                  <AddressForm
                    city={isDefaultLocation ? '' : newDto.city}
                    postalCode={isDefaultLocation ? '' : newDto.postalCode}
                    address={isDefaultLocation ? '' : newDto.address}
                    department={isDefaultLocation ? '' : newDto.department}
                    country={isDefaultLocation ? '' : newDto.country}
                    type={newDto.type}
                    onChange={(result, valid) => {
                      setNewDto((prev) => {
                        const newDto = new MysteryShopperAddressDto();
                        newDto.init(result);
                        return newDto;
                      });
                    }}
                  />
                  {suspiciousAddress && (
                    <Pressable
                      onPress={() => {
                        handleConfirmAddressForm(newDto, true);
                      }}
                    >
                      <Label style={{ color: theme.text.darkTurquoiseTwo, fontSize: 12, fontWeight: '600', flexShrink: 1 }}>
                        {t('Is-this-the-correct-address-Continue-with-this-add')}
                      </Label>
                    </Pressable>
                  )}
                </>
              }
            </ProfileModal>
          </Popup>,
        ),
      );
      dispatch(
        lightBoxActions.setOnClose(() => {
          handleOnCloseAddressForm();
        }),
      );
    }
  }, [showAddressForm, newDto, suspiciousAddress, confirmedAddress, loading]);

  const showAddressRequestPopup = (callback: () => void) => {
    if (showAddressPopup || addAddressMutation.isLoading || awaitingAddressRefresh) {
      return;
    }

    setShowAddressPopup(true);
    setCallback(() => callback);
    dispatch(lightBoxActions.setNoDismiss(true));
    dispatch(
      lightBoxActions.setContent(
        <AddressRequestPopup isDefaultLocation={isDefaultLocation} dto={dto} onConfirm={handleOnConfirmAddressRequest} onEdit={handleOnEditAddressRequest} />,
      ),
    );
    dispatch(lightBoxActions.setVisibility(true));
  };

  const addressManager = {
    showAddressRequestPopup: showAddressRequestPopup,
  };

  return addressManager;
};
export default useAddressMangager;

